import {
  getPrimaryKey,
  PaymentMethodPrimary,
  PaymentMethodValue,
} from '@easy-expense/data-schema-v2';

type PaymentMethodIdentity = { key: string; primaryKey?: string | null };

/**
 * Detects whether reassigning a payment method to another member would collide with a
 * method that member already owns.
 *
 * When an admin/manager reassigns a payment method, `value.userID` moves alongside
 * `createdBy`, which regenerates the derived `primaryKey`. If the target member already
 * owns a method with the same identity (institution/type/digits/userID), the update would
 * leave two documents sharing a `primaryKey` — a state an update cannot merge. Callers
 * should block the save in that case.
 *
 * @param value - the payment method value after the owner has been reassigned
 * @param existingPaymentMethods - all payment methods currently cached for the org
 * @param currentKey - the document key being edited (excluded from the collision check)
 */
export function reassignmentCollidesWithExisting(
  value: PaymentMethodValue,
  existingPaymentMethods: PaymentMethodIdentity[],
  currentKey: string | undefined,
): boolean {
  const prospectiveKey = getPrimaryKey({ primary: PaymentMethodPrimary, value });
  return existingPaymentMethods.some(
    (paymentMethod) =>
      paymentMethod.primaryKey === prospectiveKey && paymentMethod.key !== currentKey,
  );
}
